<template>
  <nav :style="{ backgroundImage: showHeaderBackground ? hero : null }" class="hidden sm:block w-full z-20 absolute top-0 bg-center bg-cover">
    <div :class="{ 'bg-header/[var(--bookapp-opacity-hero)]' : showHeaderBackground }" class="h-full w-full px-2 sm:px-4 py-2.5">
      <div
        class="container items-center max-w-6xl mx-auto grid grid-cols-7 px-6 rounded-full text-action"
      >
        <div>
          <NuxtLink v-if="!isHome" to="/" class="flex items-center">
            <img v-if="$site.images.logo" :src="$site.images.logo.medium" class="mr-3 h-6 sm:h-10" :alt="businessName">
            <span v-else class="self-center text-base font-semibold whitespace-nowrap text-secondary">{{ businessName }}</span>
          </NuxtLink>
        </div>

        <div class="hidden sm:block w-full col-span-5">
          <div class="flex-grow flex justify-center p-3 flex-row space-x-3 rtl:space-x-reverse mt-0 text-sm font-medium border-0">
            <!-- Has Custom Menu -->
            <template v-if="menu">
              <div v-for="(item, key) in menu" :key="`${key}-${item.type}`">
                <template v-if="item.type === 'group' && item.children && item.children.length">
                  <LayoutElementsNavigationGroup :item="item" />
                </template>

                <template v-else-if="item.type === 'tab' || item.type === 'custom'">
                  <LayoutElementsNavigationItem :item="item" class="text-center" />
                </template>
              </div>
            </template>

            <!-- Has Default (Auto) Menu -->
            <template v-else>
              <template v-for="tab in tabs">
                <div
                  v-if="$site.tabs[tab]"
                  :key="tab"
                >
                  <LayoutElementsNavigationItem
                    :item="{ id: tab }"
                  />
                </div>
              </template>
            </template>

            <div>
              <LayoutElementsNavigationItem
                v-if="$site.tabs.user"
                :item="{ id: 'user', name: userTabName }"
              />
            </div>
          </div>
        </div>

        <div class="flex items-center justify-end">
          <button v-if="showMenuToggle" class="text-[var(--bookapp-hero-text-color)] text-xl border border-[var(--bookapp-hero-text-color)] rounded p-2" @click="toggleMenu">
            <BIcon icon="menu" />
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
const { $site, $auth } = useNuxtApp()
const layoutStore = useLayoutStore()
const route = useRoute()

const tabs = ref([
  'book',
  'timetable',
  'lastMinute',
  'giftCard',
  'pass',
  'gallery',
  'review'
])

const menu = computed(() => {
  if ($site.menu?.primary) {
    return $site.menu.primary || []
  }

  return false
})

const active = computed({
  get () {
    return layoutStore.menu
  },
  set (val) {
    layoutStore.menu = val
  }
})

const isHome = computed(() => {
  return route.path === '/'
})

const isLoggedIn = computed(() => {
  return $auth?.isLoggedIn.value
})

const user = computed(() => {
  return $auth?.user.value
})


const isAppLayout = computed(() => {
  return $store?.getters.layout === 'app'
})

const isCustomPage = computed(() => {
  return route.path.includes('/page/')
})

const businessName = computed(() => {
  if ($site.tabs.contact && $site.tabs.contact.business) {
    return $site.tabs.contact.business
  } else {
    return $site.location.title
  }
})

const userTabName = computed(() => {
  if (isLoggedIn.value && user.value) {
    return user.value.displayName
  } else {
    return $site.tabs.getName('user', 'Customer')
  }
})

const showHeaderBackground = computed(() => {
  if (isHome.value) {
    return false
  }

  if (isCustomPage.value) {
    const customPages = $site.customPages || {}
    const customPage = Object.values(customPages).find(page =>
      page.slug?.toLowerCase() === route.params.slug?.toLowerCase()
    )

    if (customPage) {
      return !customPage.hero && customPage.showTitle
    }
  }

  return true
})

const hero = computed(() => {
  return $site.images.heroImageCssUrl('large')
})

const showMenuToggle = computed(() => {
  return $site.menu?.primary || $site.tabs.user || $site.customPages?.length > 0
})

const toggleMenu = () => {
  active.value = !active.value
}
</script>
